import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';
import { getArticleBySlug, articles, getRelatedArticles } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-install-01': 'https://readdy.ai/api/search-image?query=Smartphone%20showing%20app%20download%20screen%20with%20download%20button%2C%20clean%20minimal%20interface%20design%2C%20warm%20ambient%20lighting%2C%20modern%20mobile%20device%20on%20wooden%20desk%2C%20soft%20shadows%2C%20editorial%20photography%2C%20cozy%20workspace%20atmosphere&width=1200&height=600&seq=article-install-01&orientation=landscape',
  'article-tips-01': 'https://readdy.ai/api/search-image?query=Person%20holding%20smartphone%20watching%20video%20content%20on%20couch%2C%20warm%20living%20room%20atmosphere%2C%20cozy%20evening%20lighting%2C%20relaxed%20lifestyle%20scene%2C%20minimalist%20interior%20design%2C%20soft%20ambient%20glow%2C%20editorial%20photography%20style&width=1200&height=600&seq=article-tips-01&orientation=landscape',
  'article-features-01': 'https://readdy.ai/api/search-image?query=Modern%20digital%20tablet%20displaying%20video%20streaming%20interface%20with%20colorful%20thumbnails%2C%20clean%20UI%20design%2C%20warm%20studio%20lighting%2C%20technology%20concept%2C%20minimalist%20composition%2C%20professional%20product%20photography&width=1200&height=600&seq=article-features-01&orientation=landscape',
  'article-vip-01': 'https://readdy.ai/api/search-image?query=Premium%20membership%20card%20concept%20with%20golden%20accents%2C%20luxurious%20warm%20lighting%2C%20elegant%20minimal%20design%2C%20exclusive%20VIP%20aesthetic%2C%20soft%20depth%20of%20field%2C%20editorial%20photography%20style&width=1200&height=600&seq=article-vip-01&orientation=landscape',
  'article-faq-01': 'https://readdy.ai/api/search-image?query=Help%20desk%20support%20concept%20with%20question%20marks%20and%20chat%20bubbles%2C%20warm%20professional%20atmosphere%2C%20clean%20modern%20office%20setting%2C%20soft%20natural%20lighting%2C%20minimalist%20design%2C%20approachable%20friendly%20vibe&width=1200&height=600&seq=article-faq-01&orientation=landscape',
  'article-android-01': 'https://readdy.ai/api/search-image?query=Android%20smartphone%20with%20settings%20menu%20displayed%2C%20tech%20support%20concept%2C%20warm%20studio%20lighting%2C%20clean%20minimal%20composition%2C%20modern%20device%20on%20desk%2C%20professional%20product%20photography&width=1200&height=600&seq=article-android-01&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = slug ? getRelatedArticles(slug, 3) : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://aiyeshipin.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '爱液视频',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aiyeshipin.com/' },
      { '@type': 'ListItem', position: 2, name: '文章', item: 'https://aiyeshipin.com/article/aiye-video-app-shiyong-jiqiao' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} | 爱液视频App使用指南`}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',爱液视频app,爱液视频'}
        canonicalUrl={`https://aiyeshipin.com/article/${article.slug}`}
        ogType="article"
      />
      <JsonLd data={articleJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <Navbar />
      <main className="min-h-screen pt-16">
        <Breadcrumb
          items={[
            { label: '使用指南', path: '/article/aiye-video-app-shiyong-jiqiao' },
            { label: article.title },
          ]}
        />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs px-3 py-1 bg-accent-100 text-accent-800 rounded-full font-medium">
                {article.categoryName}
              </span>
              <span className="text-sm text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-base md:text-lg mb-6">{article.excerpt}</p>
            <div className="flex items-center gap-4 text-sm text-foreground-400">
              <span><strong className="text-foreground-700">{article.author}</strong></span>
              <span>发布于 {article.date}</span>
              <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()} 阅读</span>
            </div>
          </header>

          {articleImages[article.imageSeq] && (
            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={articleImages[article.imageSeq]}
                alt={article.title}
                title={`${article.title} - 爱液视频App`}
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          <div
            className="prose-custom text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-8 pt-6 border-t border-background-200">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-12">
            <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-4">相关文章</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group bg-background-100 rounded-xl p-5 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full font-medium">
                      {related.categoryName}
                    </span>
                  </div>
                  <h4 className="text-foreground-950 font-heading font-medium text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {related.title}
                  </h4>
                  <p className="text-foreground-500 text-xs line-clamp-2">{related.excerpt}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}